import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../apiBase";
const TOKEN_KEY = "mc_token";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // attach Authorization header globally — but ONLY for admin-scoped routes.
  // We must NOT attach the admin token to /api/myo/* or /api/pro/* routes:
  // they have their own auth contexts and reusing the admin token would
  // overwrite the legitimate token and trigger an instant 401 → auto-logout
  // (race condition observed on /data-room when an admin token is in localStorage).
  useEffect(() => {
    const id = axios.interceptors.request.use((cfg) => {
      const t = localStorage.getItem(TOKEN_KEY);
      if (
        t && cfg.url &&
        cfg.url.startsWith(API) &&
        !cfg.url.includes("/api/myo/") &&
        !cfg.url.includes("/api/pro/") &&
        !cfg.url.includes("/api/mssante-orders")
      ) {
        cfg.headers = { ...cfg.headers, Authorization: `Bearer ${t}` };
      }
      return cfg;
    });
    return () => axios.interceptors.request.eject(id);
  }, []);

  const fetchMe = useCallback(async () => {
    if (!localStorage.getItem(TOKEN_KEY)) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch (e) {
      localStorage.removeItem(TOKEN_KEY);
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem(TOKEN_KEY, data.token);
    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ token, user, loading, login, logout }),
    [token, user, loading, login, logout]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
